import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Box, Button, Card, CardActions, CardContent, CardHeader, Container, Grid, Stack, Typography } from "@mui/material";
import BreadcrumbBuilder from "../BreadcrumbBuilder";
import FormBuilder from "../../../Common/FormBuilder/FormBuilder";
import QRCode from "qrcode.react";

import SendIcon from "@mui/icons-material/Send";
import MSFAvatar from "../MSFAvatar";

import msfActions from "../../../../actions/msfActions";
import { getQuestionnaire } from "../../../../reducers";
import QuestionnaireDetails from "./QuestionnaireDetails";

import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
dayjs.extend(relativeTime);

function Questionnaire({ match }) {
    const dispatch = useDispatch();
    const appCode = "P-MSF";

    useEffect(() => dispatch(msfActions.getQuestionnaireDetails(appCode, match.params.uuid)), [dispatch]);

    const questionnaire = useSelector((state) => getQuestionnaire(state, match.params.uuid));
    const [responses, setResponses] = useState(
        !questionnaire?.form
            ? []
            : questionnaire.form.elements.map(({id, name, response}) => ({id, name, response}))
    );

    const title = questionnaire?.type?.name ? questionnaire.type.name : questionnaire.resourceType;

    const avatarBadge = questionnaire?.status === "closed" ? "checked" : "warning";
    const type = questionnaire?.type?.name || "Not specified";

    const canSubmit = questionnaire?.status === "open" && questionnaire?.form?.elements?.every(
        ({id, isRequired, children}) => {
            const response = responses.find((r) => r.id === id)?.response;
            return (
                (!isRequired || !!response)
                && (
                    !children?.length
                    || children.every((child) => (
                        child.isRequiredCondition !== response
                        || !!responses.find((r) => r.id === child.id)?.response
                    ))
                )
            );
        }
    );

    const submitResponses = () => {
        dispatch(msfActions.updateQuestionnaire(appCode, questionnaire.uuid, {
            form: {
                ...questionnaire.form,
                elements: responses,
            },
        }));
    };

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>
                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography={true}
                            avatar={
                                <MSFAvatar
                                    type="questionnaire"
                                    badge={avatarBadge}
                                />
                            }
                            title={
                                <Typography component="h3" className="card-header-title" >
                                    {title}
                                </Typography>
                            }
                            subheader={
                                <Typography component="span" className="card-header-subtitle" >
                                    {type}
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <QuestionnaireDetails
                                    requester={questionnaire?.requester}
                                    areaOfWork={questionnaire?.areaOfWork}
                                    organisation={questionnaire?.organisation}
                                    startDate={questionnaire?.startDate}
                                    endDate={questionnaire?.endDate}
                                />
                            </Box>
                        </CardContent>
                        <CardContent>
                            <Box className="card-content">
                                <QRCode
                                    size={100}
                                    value="https://centric.premierit.com"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                {!!questionnaire?.form && (
                    <Grid item xs={12}>
                        <Card className="card">
                            <CardHeader
                                disableTypography
                                title={
                                    <Typography component="h3" className="card-header-title" >
                                        {questionnaire.form.name}
                                    </Typography>
                                }
                            />
                            <CardContent>
                                <Box className="card-content">
                                    <FormBuilder
                                        form={questionnaire.form}
                                        canUpdate
                                        onFieldChange={({elements}) => {
                                            setResponses([
                                                ...responses.filter(({id}) => elements[0].id !== id),
                                                elements[0]
                                            ]);
                                        }}
                                    />
                                </Box>
                            </CardContent>
                            <CardActions className="card-actions">
                                <Stack
                                    direction="row"
                                    justifyContent="flex-end"
                                    alignItems="center"
                                    spacing={2}
                                    sx={{ padding: "8px" }}
                                >
                                    {!!canSubmit && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disableElevation
                                            aria-label="Submit"
                                            onClick={submitResponses}
                                            endIcon={<SendIcon />}
                                        >
                                            Submit
                                        </Button>
                                    )}
                                </Stack>
                            </CardActions>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Container>
    );
}

export default Questionnaire;
